// src/modules/forecast.js

/**
 * Build a map object
 * @param {Object} items 
 * @param {Object} params 
 * @param {Object} colors 
 * @param {String} target 
 * @returns 
 */
function getMapObject(items, params, colors, target='oil') {
  
  const precision = 6; // Number of decimal places
  const factor = Math.pow(10, precision); // 100

  items.forEach(x => {
    x[params.api] = String(x[params.api])
    x[params.latitude] = Math.round(x[params.latitude] * factor) / factor
    x[params.longitude] = Math.round(x[params.longitude] * factor) / factor
  })

  // In this order for now
  // [API10, WellName, SHLLatitude, SHLLongitude, Year, CurrentOperator, Length, oil, gas, water, Months, EUR]
  if (colors.value == 'operator') {
    return nominalMapObject(items, params)
  }
  return scalarMapObject(items, params, colors, target='oil')
  
}
/**
 * Build a scalar map object
 * @param {Object} items 
 * @param {Object} params 
 * @param {Array} colors 
 * @param {String} target 
 * @returns 
 */
function scalarMapObject(items, params, colors, target='oil') {
  const feature = colors.value == 'production' 
                  ? target : colors.value == 'operator' ? 'CurrentOperator' : colors.value

  return {
    type: "scattermap",
    text: items.map(x => x[params.api]),
    customdata: items.map(obj => Object.values(obj)),
    lat: items.map(x => x[params.latitude]),
    lon: items.map(x => x[params.longitude]),
    mode: "markers",
    // hoverinfo: 'text',
    hovertemplate: 
        "%{text}<br>" +
        "%{customdata[1]}<br>" +
        "Operator: %{customdata[5]}<br>" +
        "Started: %{customdata[4]}, Age: %{customdata[10]} months<br>" +
        "Oil: %{customdata[7]}, Gas: %{customdata[8]}, Water: %{customdata[9]}<br>" +
        "<extra></extra>",
    marker: {
      size: 6,
      reversescale: true,
      autocolorscale: false,
      color: items.map(x => x[feature]),
      colorscale: 'Portland',
      cmin: Math.min(...items.map(x => x[target])),
      cmax: Math.max(...items.map(x => x[target])),
      colorbar: {
        thickness: 10,
        titleside: "right",
        outlinecolor: "rgba(0,0,0,0)",
        ticks: "outside",
        ticklen: 3,
        x: 0.9,
        y: 0.5,
        bgcolor: "rgba(0,0,0,0)",
      },
    },
    selected: {
      marker: {
        size: 12,
        opacity: 1.0,
        color: '#F44336'
      }
    },
    unselected: {
      marker: {
        opacity: 0.1
      }
    }
  }
}
function nominalMapObject(items, params) {
  const uniqueCategories = [...new Set(items.map(x => x['CurrentOperator']))]
  const traces = []
  uniqueCategories.forEach((cat, index) => {
    const data = items.filter(x => x['CurrentOperator'] == cat)

    const hue = (index * (360 / data.length)) % 360; 
    const autoColor = `hsl(${hue}, 70%, 50%)`;

    const trace = {
      type: "scattermap",
      text: data.map(x => x[params.api]),
      customdata: data.map(obj => Object.values(obj)),
      lat: data.map(x => x[params.latitude]),
      lon: data.map(x => x[params.longitude]),
      mode: "markers",
      // hoverinfo: 'text',
      hovertemplate: 
          "%{text}<br>" +
          "%{customdata[1]}<br>" +
          "Operator: %{customdata[5]}<br>" +
          "Started: %{customdata[4]}, Age: %{customdata[10]} months<br>" +
          "Oil: %{customdata[7]}, Gas: %{customdata[8]}, Water: %{customdata[9]}<br>" +
          "<extra></extra>",
      marker: {
        size: 6,
        color: autoColor,
      },
      selected: {
        marker: {
          size: 12,
          opacity: 1.0,
          color: '#F44336'
        }
      },
      unselected: {
        marker: {
          opacity: 0.1
        }
      },
      subplot: 'map'
    }
    traces.push(trace)
  })
  console.log(traces)
  return traces
}
/**
 * Applies ARPS function to array with parameters
 * @param {Number} months 
 * @param {Number} Q 
 * @param {Number} D 
 * @param {Number} B 
 * @returns {Array}
 */
function declineWell(months, Q, D, B) {
  return Array.from(Array(months), (D, i) => i+1).map(t => arps(Q, D, B, t))
}
/**
 * Calculate instantaneous flow rate of a hyperbolic or exponential decline curve at time t
 * 
 * @param {Number} Q Initial rate (constant)
 * @param {Number} D Initial decline rate (constant)
 * @param {Number} B b value (constant)
 * @param {Number} t time at which you want to calculate the rate (variable)
 * @returns {Number} Instantaneous flow rate
 */
function arps(Q, D, B, t) {
  if (B == 0) { // Exponential decline
    return Q * Math.exp(-(D) * (t))
  } else if (B == 1) { // Harmonic decline
    return Q / (1 + D * (t)
    )
  } else { // Hyperbolic decline
    return Q / ((1 + B * D * (t)) ** (1 / B))
  }
}
/**
 * Estimated ultimate recovery via the ARPS equation, integrated from 0 to T
 * (same math as backend/app/libraries/forecasting/dca.py's eur()).
 *
 * @param {Number} Q Initial rate (constant)
 * @param {Number} D Initial decline rate (constant)
 * @param {Number} B b value (constant)
 * @param {Number} T Time period to integrate over
 * @returns {Number} Cumulative volume from 0 to T
 */
function eur(Q, D, B, T) {
  let qf = arps(Q, D, B, T)
  if (B == 0) {
    return (Q - qf) / D
  } else if (B == 1) {
    return (Q / D) * Math.log(Q / qf)
  } else {
    return ((Q ** B) / ((1 - B) * D)) * ((Q ** (1 - B)) - (qf ** (1 - B)))
  }
}
/**
 * Effective secant decline rate for any ARPS decline type (same math as
 * backend/app/libraries/forecasting/dca.py's effective_sec_decline()).
 *
 * @param {Number} D Nominal decline rate, in the same time unit as periodsPerYear
 * @param {Number} B Hyperbolic exponent (0 = exponential, 1 = harmonic)
 * @param {Number} periodsPerYear Periods per year for D's time unit (12 for monthly, 365.25 for daily)
 * @returns {Number} Effective secant decline as a fraction (multiply by 100 for a percentage)
 */
function effectiveSecDecline(D, B, periodsPerYear) {
  let Da = D * periodsPerYear
  if (B == 0) {
    return 1 - Math.exp(-Da)
  } else if (B == 1) {
    return Da / (1 + Da)
  } else {
    return 1 - (1 + B * Da) ** (-1 / B)
  }
}
/**
 * Calculate distance between two points
 * @param {Number} lat1 
 * @param {Number} lon1 
 * @param {Number} lat2 
 * @param {Number} lon2 
 * @param {String} unit Defaults to [M]iles also [K]ilometers, and [N]autical miles
 * @returns {Number}
 */
function getDistanceFromLatLon(lat1, lon1, lat2, lon2, unit='M') {
  // Return 0 if same points
  if ((lat1 == lat2) && (lon1 == lon2)) {
    return 0
  }
  else {
    var radlat1 = Math.PI * lat1/180
    var radlat2 = Math.PI * lat2/180
    var theta = lon1-lon2
    var radtheta = Math.PI * theta/180
    var dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta)
    if (dist > 1) {
        dist = 1
    }
    dist = Math.acos(dist)
    dist = dist * 180/Math.PI
    dist = dist * 60 * 1.1515
    if (unit=="K") { dist = dist * 1.609344 }
    if (unit=="N") { dist = dist * 0.8684 }
    return dist
  }
}

export { getMapObject, declineWell, getDistanceFromLatLon, arps, eur, effectiveSecDecline }
